import {createMachine, actions, assign, State, Interpreter} from 'xstate';
import type {
  AnyEventObject,
  BaseActionObject,
  BaseActions,
  DoneInvokeEvent,
  EventObject,
  InvokeCreator,
  MachineConfig,
  MachineOptions,
  StateMachine,
  StateSchema,
} from 'xstate/lib/types';

import type {ApiError} from '$types/errors';
import type {ApiResponse} from '$types/request-response';

const {send} = actions;

export interface ResourceMachineStateSchema extends StateSchema {
  states: {
    deleting: StateSchema;
    error: {
      states: {
        delete: StateSchema;
        get: StateSchema;
        list: StateSchema;
        head: StateSchema;
        patch: StateSchema;
        post: StateSchema;
        put: StateSchema;
      };
    };
    gettingList: StateSchema;
    getting: StateSchema;
    idle: StateSchema;
    heading: StateSchema;
    patching: StateSchema;
    posting: StateSchema;
    putting: StateSchema;
    success: {
      states: {
        delete: StateSchema;
        get: StateSchema;
        list: StateSchema;
        head: StateSchema;
        patch: StateSchema;
        post: StateSchema;
        put: StateSchema;
      };
    };
  };
}

type EventData<TPayload> = {data?: TPayload};
type DoneEvent<TEntity> = DoneInvokeEvent<ApiResponse<TEntity>>;
interface ResourceMachineEventObject extends EventObject, AnyEventObject {}

type Payload = Record<string, unknown>;

export interface DeleteEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'DELETE';
}
export interface ListEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'GET_LIST';
}
export interface GetEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'GET';
}
export interface HeadEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'HEAD';
}
export interface PatchEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'PATCH';
}
export interface PostEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'POST';
}
export interface PutEvent<TPayload = Payload> extends EventData<TPayload> {
  type: 'PUT';
}
export interface ResetEvent extends EventData<never> {
  type: 'RESET';
}

interface DelayResetEvent extends EventData<never> {
  type: 'DELAY_RESET';
  wait?: number;
}

export type ResourceMachineEvent<TPayload = Record<string, unknown>> =
  | DeleteEvent<TPayload>
  | ListEvent<TPayload>
  | GetEvent<TPayload>
  | HeadEvent<TPayload>
  | PatchEvent<TPayload>
  | PostEvent<TPayload>
  | PutEvent<TPayload>
  | ResetEvent
  | DelayResetEvent;

export interface ResourceMachineContext<TEntity = Record<string, unknown>>
  extends Record<string, unknown> {
  error: ApiError;
  item?: TEntity;
  items: TEntity[];
}

export type ResourceStateMachine<
  TEntity,
  TPayload = Record<string, unknown>,
> = StateMachine<
  ResourceMachineContext<TEntity>,
  ResourceMachineStateSchema,
  ResourceMachineEvent<TPayload>
>;

export type ResourceState<TEntity, TPayload> = State<
  ResourceMachineContext<TEntity>,
  ResourceMachineEvent<TPayload>
>;

export type ResourceService<
  TEntity,
  TPayload = Record<string, unknown>,
> = Interpreter<
  ResourceMachineContext<TEntity>,
  ResourceMachineStateSchema,
  ResourceMachineEvent<TPayload>
>;

export type ResourceMachineOptions<
  TResponseEntity,
  TPayload = Record<string, unknown>,
> = Partial<
  MachineOptions<
    ResourceMachineContext<TResponseEntity>,
    ResourceMachineEvent<TPayload>
  >
>;

export type ResourceMachineConfig = MachineConfig<
  ResourceMachineContext,
  ResourceMachineStateSchema,
  ResourceMachineEvent
>;

type ResourceBaseActions = BaseActions<
  ResourceMachineContext,
  ResourceMachineEvent,
  BaseActionObject
>;

enum Method {
  'delete' = 'delete',
  'get' = 'get',
  'list' = 'list',
  'head' = 'head',
  'patch' = 'patch',
  'post' = 'post',
  'put' = 'put',
}

const noop: InvokeCreator<
  ResourceMachineContext,
  ResourceMachineEvent
> = () => {
  return Promise.resolve({});
};

/**
 * Generate action name from method
 *
 * e.g. getActionName('patch', 'Success') => `onPatchSuccess`
 */
function getActionName(method: Method, suffix: string) {
  const capitalizedMethod = `${method[0].toUpperCase()}${method.slice(1)}`;

  return `on${capitalizedMethod}${suffix}`;
}

/**
 * doneActions and errorActions
 *
 * before and after hooks allow for one to optionally provide actions inside
 * components
 *
 * _handleSuccess and _handleError are used internally in this machine, and should
 * be avoided unless one doesn't want context putd, or wants to handle context
 * manually
 */
const getDoneActions = (method: Method): ResourceBaseActions => [
  '_handleSuccess',
  getActionName(method, 'Success'),
];
const getErrorActions = (method: Method): ResourceBaseActions => [
  '_handleError',
  getActionName(method, 'Error'),
];

const defaultContext: ResourceMachineContext = {
  error: {
    messages: [],
  },
  items: [],
};

const methodEventMap = {
  DELETE: 'deleting',
  GET: 'getting',
  GET_LIST: 'gettingList',
  HEAD: 'heading',
  PATCH: 'patching',
  POST: 'posting',
  PUT: 'putting',
};
const resetEventMap = {
  DELAY_RESET: {actions: '_handleDelayedReset'},
  RESET: 'idle',
};

const defaultConfig: ResourceMachineConfig = {
  predictableActionArguments: true,

  id: 'resource-machine',

  initial: 'idle',

  context: {...defaultContext},

  states: {
    idle: {on: methodEventMap},

    posting: {
      invoke: {
        id: 'post',
        src: 'post',

        onDone: {target: 'success.post'},
        onError: {target: 'error.post'},
      },
    },

    getting: {
      invoke: {
        id: 'get',
        src: 'get',

        onDone: {target: 'success.get'},
        onError: {target: 'error.get'},
      },
    },

    gettingList: {
      invoke: {
        id: 'list',
        src: 'list',

        onDone: {target: 'success.list'},
        onError: {target: 'error.list'},
      },
    },

    heading: {
      invoke: {
        id: 'head',
        src: 'head',

        onDone: {target: 'success.head'},
        onError: {target: 'error.head'},
      },
    },

    putting: {
      invoke: {
        id: 'put',
        src: 'put',

        onDone: {target: 'success.put'},
        onError: {target: 'error.put'},
      },
    },

    patching: {
      invoke: {
        id: 'patch',
        src: 'patch',

        onDone: {target: 'success.patch'},
        onError: {target: 'error.patch'},
      },
    },

    deleting: {
      invoke: {
        id: 'delete',
        src: 'delete',

        onDone: {target: 'success.delete'},
        onError: {target: 'error.delete'},
      },
    },

    success: {
      on: {...methodEventMap, ...resetEventMap},

      states: {
        delete: {entry: getDoneActions(Method.delete)},
        get: {entry: getDoneActions(Method.get)},
        list: {entry: getDoneActions(Method.list)},
        head: {entry: getDoneActions(Method.head)},
        patch: {entry: getDoneActions(Method.patch)},
        post: {entry: getDoneActions(Method.post)},
        put: {entry: getDoneActions(Method.put)},
      },
    },

    error: {
      on: {...methodEventMap, ...resetEventMap},

      states: {
        delete: {entry: getErrorActions(Method.delete)},
        get: {entry: getErrorActions(Method.get)},
        list: {entry: getErrorActions(Method.list)},
        head: {entry: getErrorActions(Method.head)},
        patch: {entry: getErrorActions(Method.patch)},
        post: {entry: getErrorActions(Method.post)},
        put: {entry: getErrorActions(Method.put)},
      },
    },
  },
};

const defaultOptions = {
  actions: {
    _handleError: assign<ResourceMachineContext, ResourceMachineEventObject>(
      (_, {data}) => ({
        error: data,
      }),
    ),

    onDeleteError: noop,
    onListError: noop,
    onGetError: noop,
    onHeadError: noop,
    onPatchError: noop,
    onPostError: noop,
    onPutError: noop,

    _handleSuccess: assign((_, event) => {
      const {data} = event as DoneEvent<Record<string, unknown>>;
      const property = Array.isArray(data) ? 'items' : 'item';

      return {[property]: data};
    }),

    onDeleteSuccess: noop,
    onListSuccess: noop,
    onGetSuccess: noop,
    onHeadSuccess: noop,
    onPatchSuccess: noop,
    onPostSuccess: noop,
    onPutSuccess: noop,

    /**
     * Allow resource to reset itself after a delay
     */
    _handleDelayedReset: send('RESET', {
      delay: (_, event) => {
        const {wait} = event as DelayResetEvent;

        return wait || 0;
      },
    }),
  },

  services: {
    delete: noop,
    get: noop,
    list: noop,
    head: noop,
    patch: noop,
    post: noop,
    put: noop,
  },
};

export interface CreateResourceMachineFn {
  <TEntity, TPayload>(id: string): ResourceStateMachine<TEntity, TPayload>;
}
const createResourceMachine: CreateResourceMachineFn = <TEntity, TPayload>(
  id = 'resource',
) => {
  const config: Partial<ResourceMachineConfig> = {...defaultConfig, id};
  const machine = createMachine(
    config,
    defaultOptions as unknown,
  ) as unknown as ResourceStateMachine<TEntity, TPayload>;

  return machine;
};

export {defaultContext, defaultConfig, defaultOptions, createResourceMachine};
